import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import colors from '../../styles/colors';

const SUPPORT_EMAIL = 'jeff@visibilitylabs.ai';

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: colors.bg.offWhite,
  },
  header: {
    background: colors.bg.white,
    padding: '16px 0',
    borderBottom: `1px solid ${colors.border.lightNavy}`,
  },
  headerInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImg: { height: '34px', width: 'auto' },
  main: { flex: 1, padding: '48px 0' },
  mainInner: { maxWidth: '980px', margin: '0 auto', padding: '0 24px' },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: colors.text.muted,
    marginBottom: '18px',
    lineHeight: '1.7',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    borderRadius: '999px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    fontSize: '13px',
    fontWeight: '800',
    color: colors.text.darkNavy,
  },
  card: {
    background: colors.bg.white,
    border: `1px solid ${colors.border.lightNavy}`,
    borderRadius: '12px',
    padding: '18px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  promptRow: {
    display: 'grid',
    gridTemplateColumns: '56px 1fr 48px',
    gap: '14px',
    padding: '14px 0',
    borderBottom: `1px solid ${colors.border.light}`,
    alignItems: 'start',
  },
  promptLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'flex-start',
  },
  index: {
    fontSize: '12px',
    color: colors.text.muted,
    fontWeight: '700',
  },
  promptInput: {
    width: '100%',
    minHeight: '44px',
    borderRadius: '10px',
    border: `1px solid ${colors.border.light}`,
    padding: '10px 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  promptInputRejected: {
    border: '1px solid rgba(220, 38, 38, 0.55)',
    background: 'rgba(220, 38, 38, 0.04)',
  },
  rowMeta: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  actionsCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: '2px',
  },
  editedTag: {
    display: 'inline-block',
    fontSize: '12px',
    fontWeight: '800',
    color: colors.text.purple,
  },
  rejectedTag: {
    display: 'inline-block',
    fontSize: '12px',
    fontWeight: '800',
    color: '#b91c1c',
  },
  rejectBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '999px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    cursor: 'pointer',
    padding: 0,
  },
  rejectBtnActive: {
    border: '1px solid rgba(220, 38, 38, 0.55)',
    background: 'rgba(220, 38, 38, 0.06)',
  },
  rejectBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  commentsLabel: {
    marginTop: '18px',
    display: 'block',
    fontSize: '13px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  commentsBox: {
    width: '100%',
    minHeight: '140px',
    resize: 'vertical',
    borderRadius: '10px',
    border: `1px solid ${colors.border.light}`,
    padding: '12px 14px',
    fontSize: '14px',
    fontFamily: 'inherit',
    lineHeight: '1.6',
    outline: 'none',
  },
  actions: { marginTop: '18px' },
  button: {
    background: colors.bg.primaryPurple,
    color: colors.text.white,
    border: 'none',
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: '800',
  },
  errorBanner: {
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
    color: '#7f1d1d',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '18px',
    fontSize: '14px',
    fontWeight: '700',
  },
  thankYouWrap: {
    background: `linear-gradient(135deg, ${colors.bg.primaryPurple} 0%, ${colors.bg.darkNavy} 100%)`,
    borderRadius: '16px',
    padding: '28px',
    marginBottom: '24px',
    color: colors.text.white,
    textAlign: 'center',
    boxShadow: '0 12px 30px rgba(23, 15, 73, 0.18)',
  },
  thankYouIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '18px',
    background: 'rgba(255, 255, 255, 0.14)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    margin: '0 auto 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
  },
  thankYouTitle: {
    fontSize: '28px',
    fontWeight: '800',
    marginBottom: '10px',
    letterSpacing: '-0.5px',
  },
  thankYouText: {
    maxWidth: '620px',
    margin: '0 auto',
    fontSize: '15px',
    lineHeight: '1.7',
    color: 'rgba(255,255,255,0.86)',
  },
  footer: {
    background: colors.bg.darkNavy,
    padding: '22px 0',
    textAlign: 'center',
  },
  footerText: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
};

function PromptsFeedback() {
  const { uuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [locked, setLocked] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const [clientName, setClientName] = useState('');
  const [reviewer, setReviewer] = useState('Client');
  const [promptsOriginal, setPromptsOriginal] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [comments, setComments] = useState('');
  const [commentsError, setCommentsError] = useState(false);
  const [rejectedIds, setRejectedIds] = useState(() => new Set());

  const detailUrl = useMemo(() => `/forms/prompts-feedback/${uuid}/`, [uuid]);
  const commentsRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    setNotFound(false);

    api
      .get(detailUrl)
      .then((res) => {
        const data = res.data || {};
        const isLocked = Boolean(data?.is_locked);
        setLocked(isLocked);
        setShowThankYou(isLocked);
        setClientName(data?.client_name || '');
        setReviewer(data?.reviewer || 'Client');

        const orig = (data?.prompts_original || []).map((p) => ({
          Id: p.Id,
          Prompt: typeof p.Prompt === 'string' ? p.Prompt.replaceAll('\\n', '\n') : p.Prompt,
        }));
        const working = (data?.prompts_working || []).map((p) => ({
          Id: p.Id,
          Prompt: typeof p.Prompt === 'string' ? p.Prompt.replaceAll('\\n', '\n') : p.Prompt,
        }));

        setPromptsOriginal(orig);
        setPrompts(working.length ? working : orig);
        setComments((data?.additional_comments || '').replaceAll('\\n', '\n'));
        setRejectedIds(new Set((data?.rejected_prompt_ids || []).map((x) => Number(x))));
        setLoading(false);
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || 'This form link is invalid.';
        setError(msg);
        setNotFound(err?.response?.status === 404);
        setLoading(false);
      });
  }, [detailUrl]);

  const editedCount = useMemo(() => {
    const byId = new Map(promptsOriginal.map((p) => [p.Id, p.Prompt || '']));
    let count = 0;
    for (const p of prompts) {
      const base = byId.get(p.Id) ?? '';
      if ((p.Prompt || '') !== base) count += 1;
    }
    return count;
  }, [prompts, promptsOriginal]);

  const filteredPrompts = prompts;

  const updatePrompt = (id, text) => {
    setPrompts((prev) => prev.map((p) => (p.Id === id ? { ...p, Prompt: text } : p)));
  };

  const toggleRejected = (id) => {
    setRejectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setCommentsError(false);
  };

  const rejectedCount = useMemo(() => rejectedIds.size, [rejectedIds]);


  const onSubmit = (e) => {
    e.preventDefault();
    if (locked || submitting) return;

    if (reviewer === 'Client' && rejectedIds.size > 0 && !comments.trim()) {
      setCommentsError(true);
      commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      commentsRef.current?.focus();
      return;
    }

    setCommentsError(false);
    setSubmitting(true);
    setError('');

    api
      .post(`${detailUrl}submit/`, {
        prompts,
        additional_comments: comments,
        rejected_prompt_ids: Array.from(rejectedIds),
      })
      .then(() => {
        setLocked(true);
        setShowThankYou(true);
        setSubmitting(false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail ||
          'Could not submit the form. Please try again or contact support.';
        setError(msg);
        setSubmitting(false);
      });
  };

  return (
    <div style={styles.wrapper}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <a href="https://visibilitylabs.ai" aria-label="Go to visibilitylabs.ai">
            <img src="/visibilitylabs.svg" alt="Visibility Labs" style={styles.logoImg} />
          </a>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.mainInner}>
          {loading ? (
            <div style={styles.card}>Loading form…</div>
          ) : (
            <>
              {notFound && (
                <div style={styles.errorBanner}>
                  Link not found. If you believe this is an error, email{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: colors.text.accentBlue, fontWeight: 800 }}>
                    {SUPPORT_EMAIL}
                  </a>.
                </div>
              )}

              {showThankYou && !notFound && (
                <div style={styles.thankYouWrap}>
                  <div style={styles.thankYouIcon}>✓</div>
                  <div style={styles.thankYouTitle}>Thank you!</div>
                  <div style={styles.thankYouText}>
                    Thanks for taking the time to review and approve the prompts for{' '}
                    {clientName || 'your company'}.
                    <br />
                    We’ve received your feedback and will reach out with any further questions.
                    <br />
                    Full speed ahead!
                  </div>
                </div>
              )}

              {!notFound && (
                <>
                  <h1 style={styles.title}>
                    {clientName ? `${clientName} - Prompt Feedback` : 'Prompt Feedback'}
                  </h1>
                  <p style={styles.subtitle}>
                    Please review the prompts below.
                    <br />
                    You can edit the prompts directly, reject a prompt altogether, and add comments at the end with
                    anything you’d like to tell us.
                    <br />
                    When everything looks right, click <b>Approve &amp; Submit</b> at the bottom.
                  </p>

                  {error && <div style={styles.errorBanner}>{error}</div>}

                  <div style={styles.toolbar}>
                    <div style={styles.pill}>
                      Edited: {editedCount}/{prompts.length}
                    </div>
                    <div style={styles.pill}>Rejected: {rejectedCount}</div>
                  </div>

                  <form onSubmit={onSubmit} style={styles.card}>
                    {filteredPrompts.map((p, idx) => {
                      const original = promptsOriginal.find((x) => x.Id === p.Id)?.Prompt || '';
                      const isEdited = (p.Prompt || '') !== original;
                      const isRejected = rejectedIds.has(p.Id);
                      return (
                        <div key={p.Id} style={styles.promptRow}>
                          <div style={styles.promptLeft}>
                            <div style={styles.index}>#{idx + 1}</div>
                          </div>
                          <div>
                            <input
                              style={{
                                ...styles.promptInput,
                                ...(isRejected ? styles.promptInputRejected : {}),
                              }}
                              value={p.Prompt || ''}
                              disabled={locked}
                              onChange={(e) => updatePrompt(p.Id, e.target.value)}
                            />
                            <div style={styles.rowMeta}>
                              {isEdited && <div style={styles.editedTag}>Edited</div>}
                              {isRejected && <div style={styles.rejectedTag}>Rejected</div>}
                            </div>
                          </div>
                          <div style={styles.actionsCol}>
                            <button
                              type="button"
                              title={isRejected ? 'Unreject prompt' : 'Reject prompt'}
                              aria-label={isRejected ? 'Unreject prompt' : 'Reject prompt'}
                              onClick={() => toggleRejected(p.Id)}
                              disabled={locked}
                              style={{
                                ...styles.rejectBtn,
                                ...(isRejected ? styles.rejectBtnActive : {}),
                                ...(locked ? styles.rejectBtnDisabled : {}),
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                  d="M6 6 L18 18 M18 6 L6 18"
                                  stroke="#ef4444"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {reviewer !== 'PM' && (
                      <>
                        <label style={{
                          ...styles.commentsLabel,
                          ...(commentsError ? { color: '#b91c1c' } : {}),
                        }}>
                          Additional Comments
                          {reviewer === 'Client' && rejectedIds.size > 0
                            ? <span style={{ color: '#ef4444' }}> * (required — please explain why you rejected the prompt{rejectedIds.size > 1 ? 's' : ''})</span>
                            : <span style={{ color: colors.text.muted, fontWeight: 400 }}> (optional)</span>
                          }
                        </label>
                        {commentsError && (
                          <div style={{
                            fontSize: '13px',
                            color: '#b91c1c',
                            fontWeight: '700',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}>
                            ⚠ Please tell us why you rejected the prompt{rejectedIds.size > 1 ? 's' : ''} before submitting.
                          </div>
                        )}
                        <textarea
                          ref={commentsRef}
                          style={{
                            ...styles.commentsBox,
                            ...(commentsError ? {
                              border: '1.5px solid #ef4444',
                              background: 'rgba(220, 38, 38, 0.04)',
                              outline: 'none',
                            } : {}),
                          }}
                          value={comments}
                          disabled={locked}
                          onChange={(e) => {
                            setComments(e.target.value);
                            if (commentsError && e.target.value.trim()) setCommentsError(false);
                          }}
                          placeholder={
                            reviewer === 'Client' && rejectedIds.size > 0
                              ? 'Please explain why you rejected the prompts and what changes are needed…'
                              : 'Any notes about prompt quality, intent, or missing topics…'
                          }
                        />
                      </>
                    )}

                    <div style={styles.actions}>
                      {!locked && (
                        <>
                          <button type="submit" style={styles.button}>
                            {submitting ? 'Submitting…' : reviewer === 'PM' ? 'Approve and Send to Client' : 'Approve & Submit'}
                          </button>
                          {reviewer === 'PM' && (
                            <p style={{
                              marginTop: '10px',
                              fontSize: '13px',
                              color: colors.text.muted,
                              lineHeight: '1.6',
                            }}>
                              By clicking this button your changes (Edits/Rejects) will be implemented in DB and Sent to client automatically.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </main>

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          Visibility Labs &copy; {new Date().getFullYear()}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default PromptsFeedback;

